import { addDays, format, startOfMonth, subMonths } from 'date-fns'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ApiError, unwrap } from 'api/call'
import {
  getDailyReports,
  getDailyReportsOverview,
  getDailyReportsSources,
  getDailyReportsUpcomingRenewals,
  type DailyReportResponse,
  type DailyReportSource,
  type GetDailyReportsData,
  type UpcomingRenewalsResponse,
} from 'api/generated'
import { toast } from 'sonner'

type Query = GetDailyReportsData['query']
type SourceKey = Query['source']
export type OverviewQuery = Pick<Query, 'preset' | 'granularity'>

interface DailyReportState {
  sources: DailyReportSource[]
  // Dashboard "Dettaglio": one source at a time
  report: DailyReportResponse | null
  loading: boolean
  error: string | null
  // Dashboard "Panoramica": every source for the same period
  overview: Partial<Record<SourceKey, DailyReportResponse>>
  // Recharge by month: the chosen months plus the 12 before, for the year-on-year comparison
  revenueMonths: DailyReportResponse | null
  // Live from Recharge and the premium expirations: today and the next days
  upcomingRenewals: UpcomingRenewalsResponse | null
  upcomingRenewalsLoading: boolean
  overviewLoading: boolean
  overviewError: string | null
}

const initialState: DailyReportState = {
  sources: [],
  report: null,
  loading: false,
  error: null,
  overview: {},
  revenueMonths: null,
  upcomingRenewals: null,
  upcomingRenewalsLoading: false,
  overviewLoading: false,
  overviewError: null,
}

// The catalog never changes mid-session: fetched once on mount
export const fetchDailyReportSources = createAsyncThunk<
  DailyReportSource[],
  void,
  { rejectValue: string }
>('dailyReport/fetchSources', async (_, { rejectWithValue }) => {
  try {
    return (await unwrap(getDailyReportsSources())).sources
  } catch {
    return rejectWithValue('Errore nel caricamento delle fonti.')
  }
})

export const fetchDailyReport = createAsyncThunk<
  DailyReportResponse,
  Query,
  { rejectValue: string }
>('dailyReport/fetchReport', async (query, { rejectWithValue }) => {
  try {
    return await unwrap(getDailyReports({ query }))
  } catch (error) {
    // 422: period too long for daily rows, invalid dates
    return rejectWithValue(
      error instanceof ApiError && error.status === 422
        ? error.message
        : 'Errore nel caricamento del report.'
    )
  }
})

// Every source for the same period in one call (the API has a per-token rate limit)
export const fetchDailyReportOverview = createAsyncThunk<
  DailyReportState['overview'],
  OverviewQuery,
  { rejectValue: string }
>('dailyReport/fetchOverview', async (query, { rejectWithValue }) => {
  try {
    const { reports } = await unwrap(getDailyReportsOverview({ query }))
    return Object.fromEntries(reports.map((r) => [r.source, r]))
  } catch {
    return rejectWithValue('Errore nel caricamento del report giornaliero.')
  }
})

// Recharge by month over the last `months` months (the current one included) and
// the 12 before them, for the same month of the previous year: independent of the
// chosen period
export const fetchRevenueMonths = createAsyncThunk<
  DailyReportResponse,
  number,
  { rejectValue: string }
>('dailyReport/fetchRevenueMonths', async (months, { rejectWithValue }) => {
  const today = new Date()
  try {
    return await unwrap(
      getDailyReports({
        query: {
          source: 'recharge',
          from: format(
            startOfMonth(subMonths(today, months - 1 + 12)),
            'yyyy-MM-dd'
          ),
          to: format(today, 'yyyy-MM-dd'),
          granularity: 'month',
        },
      })
    )
  } catch {
    return rejectWithValue('Errore nel caricamento dei ricavi per mese.')
  }
})

// Today and the next `days` days
export const fetchUpcomingRenewals = createAsyncThunk<
  UpcomingRenewalsResponse,
  number,
  { rejectValue: string }
>('dailyReport/fetchUpcomingRenewals', async (days, { rejectWithValue }) => {
  const today = new Date()
  try {
    return await unwrap(
      getDailyReportsUpcomingRenewals({
        query: {
          from: format(today, 'yyyy-MM-dd'),
          to: format(addDays(today, days), 'yyyy-MM-dd'),
        },
      })
    )
  } catch {
    return rejectWithValue('Errore nel caricamento dei rinnovi in arrivo.')
  }
})

const dailyReportSlice = createSlice({
  name: 'dailyReport',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDailyReportSources.fulfilled, (state, action) => {
      state.sources = action.payload
    })
    builder.addCase(fetchDailyReportSources.rejected, (_state, action) => {
      toast.error(action.payload || 'Errore nel caricamento delle fonti')
    })
    builder.addCase(fetchDailyReport.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchDailyReport.fulfilled, (state, action) => {
      state.loading = false
      state.report = action.payload
    })
    builder.addCase(fetchDailyReport.rejected, (state, action) => {
      state.loading = false
      state.report = null
      state.error = action.payload || 'Impossibile caricare il report'
      toast.error(state.error)
    })
    builder.addCase(fetchDailyReportOverview.pending, (state) => {
      state.overviewLoading = true
      state.overviewError = null
    })
    builder.addCase(fetchDailyReportOverview.fulfilled, (state, action) => {
      state.overviewLoading = false
      state.overview = action.payload
    })
    builder.addCase(fetchDailyReportOverview.rejected, (state, action) => {
      state.overviewLoading = false
      state.overviewError =
        action.payload || 'Impossibile caricare il report giornaliero'
      toast.error(state.overviewError)
    })
    builder.addCase(fetchRevenueMonths.fulfilled, (state, action) => {
      state.revenueMonths = action.payload
    })
    builder.addCase(fetchRevenueMonths.rejected, (state, action) => {
      state.revenueMonths = null
      toast.error(action.payload || 'Errore nel caricamento dei ricavi')
    })
    builder.addCase(fetchUpcomingRenewals.pending, (state) => {
      state.upcomingRenewalsLoading = true
    })
    builder.addCase(fetchUpcomingRenewals.fulfilled, (state, action) => {
      state.upcomingRenewalsLoading = false
      state.upcomingRenewals = action.payload
    })
    builder.addCase(fetchUpcomingRenewals.rejected, (state, action) => {
      state.upcomingRenewalsLoading = false
      state.upcomingRenewals = null
      toast.error(action.payload || 'Errore nel caricamento dei rinnovi')
    })
  },
})

export const dailyReportReducer = dailyReportSlice.reducer
